import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Markdown } from "../../components/Markdown";
import { Card, PageHeader } from "../../components/ui";
import { docBySlug, headingsOf, sectionOf } from "../../docs";
import { NotFoundPage } from "../NotFoundPage";
import { LIBRARY } from "./DocsLayout";

/** One document from the library, with "on this page" beside it. */
export function DocPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const doc = docBySlug(slug);
  const source = doc ? (doc.section ? sectionOf(doc.markdown, doc.section) : doc.markdown.replace(/^# .*\n/, "")) : "";
  const headings = headingsOf(source);

  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
  }, [hash, slug]);

  if (!doc) return <NotFoundPage />;
  return (
    <>
      <PageHeader title={doc.title} subtitle={doc.summary} />
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_13rem] lg:items-start lg:gap-8">
        <Card>
          <article className="max-w-3xl">
            <Markdown source={source} />
          </article>
        </Card>
        {headings.length > 1 && (
          <nav aria-label="On this page" className="mt-6 lg:sticky lg:top-4 lg:mt-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">On this page</p>
            <ul className="mt-2 space-y-1 text-sm">
              {headings.map((h) => (
                <li key={h.id} className={h.level === 3 ? "pl-3" : ""}>
                  <Link to={`#${h.id}`} className="text-neutral-700 hover:underline dark:text-neutral-300">
                    {h.text}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </>
  );
}

/** `/docs` itself: the shelf. */
export function DocsIndexPage() {
  return (
    <>
      <PageHeader title="Documentation" subtitle="Everything a customer needs to deploy, operate and sign in to Starsach - the same files that ship with your API key, kept current with every release." />
      <ul className="grid gap-4 sm:grid-cols-2">
        {LIBRARY.map((e) => (
          <li key={e.to}>
            <Link to={e.to} className="block h-full rounded-xl border border-neutral-200 bg-white p-4 transition-shadow hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900">
              <span className="block font-semibold">{e.title}</span>
              <span className="mt-1 block text-sm text-neutral-600 dark:text-neutral-400">{e.summary}</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
